import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import { useUser } from "contexts/UserContext";
import type {
  OrchidDriveDirectory,
  OrchidDriveFile,
} from "contexts/orchid-drive/types";
import messages from "messages";
import moment from "moment-timezone";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import formatFileSize from "utils/formatFileSize";
import getClasses from "utils/getClasses";
import getMIMETypeByFileName from "utils/getMIMETypeByFileName";
import type { AttachmentsViewProps, SelectedItem } from "../..";
import DropZone from "../../../../../upload-zone/components/drop-zone";
import useRenameEntry from "../../hooks/useRenameEntry";
import ActionsMenu from "../actions-menu/ActionsMenu";
import TypeIcon from "../type-icon";
import withEditable from "../with-editable";
import styles from "./ListView.module.scss";
import { ReactComponent as OutlineChevronDown16 } from "assets/svg/outline-chevron-down-16.svg";

const DATE_FORMAT = "MM/D/YY h:mm A z";

const EditableButton = withEditable(Button);
const EditableDiv = withEditable("div");

const ListView = ({
  selected = [],
  setSelected,
  handleSelect,
  dataEntryLength,
  dataInView,
  setFolderPaths,
  registerSelectableRef,
  deleteEntry,
  onDrop,
  storageDrive,
  batchEntryOps,
  onError,
  onDownload,
  isSelectedDragging,
  setIsSelectedDragging,
  onMoveEnd,
  analyticsPageName,
  setOrderByField,
  setOrderDescending,
}: AttachmentsViewProps) => {
  const { defaultUserTimezone } = useUser();
  const { showRename, openRename, onRename } = useRenameEntry({
    batchEntryOps,
    setSelected,
    onError,
  });

  const sizeRef = useRef<{
    top: number;
    height: number;
    textBottom: number;
  }>();

  const [draggingOver, setDraggingOver] = useState(false);

  const onDragFilesEnd = useCallback(
    async (item: OrchidDriveFile | OrchidDriveDirectory) => {
      if (!batchEntryOps || !isSelectedDragging) {
        return;
      }

      if ("directoryId" in item) {
        if (selected.some((el) => el.guid === item.directoryId)) {
          return;
        }

        try {
          await batchEntryOps({
            entryMoves: selected.map((el) => ({
              name: el.name,
              entryId: el.guid,
              parentDirectoryId: item.directoryId,
            })),
            entryDeletes: [],
          });
          return (
            onMoveEnd &&
            onMoveEnd({
              selected,
              targetFolder: item,
              success: true,
            })
          );
        } catch (error) {
          console.error(error);
          return (
            onMoveEnd &&
            onMoveEnd({
              selected,
              targetFolder: item,
              success: false,
            })
          );
        }
      }
    },
    [batchEntryOps, isSelectedDragging, onMoveEnd, selected],
  );

  // need for safari since :hover will not work
  const [hoverId, setHoverId] = useState("");

  useEffect(() => {
    if (!isSelectedDragging) {
      setHoverId("");
    }
  }, [isSelectedDragging]);

  return (
    <div
      className={styles["list-view__container"]}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!showRename) {
          setDraggingOver(true);
        }
      }}
    >
      {draggingOver && (
        <DropZone
          className={styles["list-view__drop-zone"]}
          storageDrive={storageDrive}
          style={{
            height: sizeRef.current?.height,
            top: sizeRef.current?.top,
          }}
          textStyle={{
            position: "fixed",
            bottom:
              sizeRef.current?.textBottom === undefined
                ? undefined
                : `min(${sizeRef.current?.textBottom}px, 25vh, 189px)`,
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDraggingOver(false);
            return onDrop(e);
          }}
          // !! need this onDragOver to make onDrop work
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDraggingOver(false);
          }}
        />
      )}

      <table className={styles["list-view"]}>
        <thead className={styles["list-view__thead"]}>
          <tr>
            <th className={styles["list-view__checkbox-col"]}>
              <CustomSelectionControl
                indeterminate={
                  selected.length > 0 && dataEntryLength > selected.length
                }
                className={styles["list-view__checkbox"]}
                type={"checkbox"}
                checked={selected.length > 0}
                onChange={(e) =>
                  setSelected &&
                  setSelected((s) =>
                    e.target.checked
                      ? (
                          [
                            [dataInView.filesInView, false],
                            [dataInView.foldersInView, true],
                          ] as const
                        ).reduce((prev, [curr, isFolder]) => {
                          curr.forEach((el) =>
                            prev.push({
                              type: isFolder
                                ? "folder"
                                : (el as OrchidDriveFile).mimeType,
                              guid: isFolder
                                ? (el as OrchidDriveDirectory).directoryId
                                : (el as OrchidDriveFile).fileDataId,
                              ...el,
                            }),
                          );
                          return prev;
                        }, [] as SelectedItem[])
                      : s.slice(s.length),
                  )
                }
              />
            </th>
            <th className={styles.headerWithDropdown}>
              <div>{messages.name}</div>
              {setOrderByField && setOrderDescending && (
                <Dropdown>
                  <Dropdown.Toggle
                    className={styles.dropdownToggle}
                    variant="link"
                    bsPrefix="p-0"
                  >
                    <OutlineChevronDown16 />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={styles.dropdownMenu}>
                    <Dropdown.Item
                      className={styles.dropdownItem}
                      onClick={() => {
                        setOrderByField("name");
                        setOrderDescending(false);
                      }}
                    >
                      A to Z
                    </Dropdown.Item>
                    <Dropdown.Item
                      className={styles.dropdownItem}
                      onClick={() => {
                        setOrderByField("name");
                        setOrderDescending(true);
                      }}
                    >
                      Z to A
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </th>
            <th>{messages.size}</th>
            <th className={styles.headerWithDropdown}>
              <div>{messages.dateAdded}</div>
              {setOrderByField && setOrderDescending && (
                <Dropdown>
                  <Dropdown.Toggle
                    className={styles.dropdownToggle}
                    variant="link"
                    bsPrefix="p-0"
                  >
                    <OutlineChevronDown16 />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={styles.dropdownMenu}>
                    <Dropdown.Item
                      className={styles.dropdownItem}
                      onClick={() => {
                        setOrderByField("createDatetime");
                        setOrderDescending(false);
                      }}
                    >
                      Ascending
                    </Dropdown.Item>
                    <Dropdown.Item
                      className={styles.dropdownItem}
                      onClick={() => {
                        setOrderByField("createDatetime");
                        setOrderDescending(true);
                      }}
                    >
                      Descending
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </th>
            {/* TODO: will be added in the new rev of BE api response */}
            <th></th>
          </tr>
        </thead>

        <tbody
          className="position-relative"
          ref={(el) => {
            if (el) {
              const { bottom, top } = el.getBoundingClientRect();

              sizeRef.current = {
                top: el.offsetTop,
                // height: el.offsetHeight,
                height:
                  bottom < window.innerHeight
                    ? window.innerHeight - top
                    : el.offsetHeight,
                textBottom: el.offsetHeight / 2 < 215 ? el.offsetHeight : 215,
              };
            }
          }}
        >
          {(
            [
              [dataInView.foldersInView, true],
              [dataInView.filesInView, false],
            ] as const
          ).map(([datum, isFolder]) => {
            return datum.map((el: OrchidDriveFile | OrchidDriveDirectory) => {
              const id = isFolder
                ? (el as OrchidDriveDirectory).directoryId
                : (el as OrchidDriveFile).fileDataId;
              const type = isFolder
                ? "folder"
                : (el as OrchidDriveFile).mimeType ||
                  getMIMETypeByFileName(el.name);
              const size = isFolder
                ? undefined
                : (el as OrchidDriveFile).uploadedSize;

              const checked = selected.some((item) => item.guid === id);
              return (
                <tr
                  onMouseOver={() => {
                    if (checked || !isSelectedDragging || !isFolder) {
                      return;
                    }
                    setHoverId(() => id);
                  }}
                  onMouseOut={() => setHoverId(() => "")}
                  className={getClasses(
                    styles["list-view__tbody-tr"],
                    checked && styles["list-view__tbody-tr--checked"],
                    checked &&
                      isSelectedDragging &&
                      styles["list-view__tbody-tr--checked-dragging"],

                    !checked &&
                      isSelectedDragging &&
                      styles["list-view__tbody-tr--unchecked-dropping"],

                    !checked &&
                      isSelectedDragging &&
                      isFolder &&
                      hoverId === id &&
                      styles["list-view__tbody-tr--unchecked-dropping--folder"],
                  )}
                  key={id}
                  onMouseDown={() => {
                    if (checked) {
                      setIsSelectedDragging(true);
                    }
                  }}
                  onMouseUp={() => onDragFilesEnd(el)}
                  // conflict w/ drag to select
                  // onClick={() => handleSelect({ type, guid })}
                  ref={(element) =>
                    registerSelectableRef &&
                    registerSelectableRef(id, {
                      element,
                      value: {
                        ...el,
                        type,
                        guid: id,
                      },
                    })
                  }
                >
                  <td
                    className={styles["list-view__checkbox-col"]}
                    // dont propagate to <tr>
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <CustomSelectionControl
                      className={styles["list-view__checkbox"]}
                      type={"checkbox"}
                      checked={selected.some((el) => el.guid === id)}
                      onChange={() =>
                        handleSelect && handleSelect({ ...el, type, guid: id })
                      }
                    />
                  </td>
                  <td className={styles["list-view__type-icon-col"]}>
                    <div
                      className={getClasses(
                        styles["list-view__file-name"],
                        isSelectedDragging &&
                          styles["list-view__file-name--dragging"],
                      )}
                    >
                      <TypeIcon mimeType={type} />

                      {isFolder ? (
                        <EditableButton
                          variant={"link"}
                          className={`${styles["list-view__file-path"]} ${styles["list-view__file-path--folder"]}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFolderPaths((v) => [
                              ...v,
                              el as OrchidDriveDirectory,
                            ]);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          show={showRename === id}
                          onUpdate={(v) =>
                            onRename(
                              {
                                name: v,
                                entryId: (el as OrchidDriveDirectory)
                                  .directoryId,
                                parentDirectoryId: el.parentDirectoryId,
                              },
                              el.name,
                            )
                          }
                        >
                          {el.name}
                        </EditableButton>
                      ) : (
                        <EditableDiv
                          className={styles["list-view__file-path"]}
                          show={showRename === id}
                          onUpdate={(v) =>
                            onRename(
                              {
                                name: v,
                                entryId: (el as OrchidDriveFile).fileDataId,
                                parentDirectoryId: el.parentDirectoryId,
                              },
                              el.name,
                            )
                          }
                        >
                          {el.name}
                        </EditableDiv>
                      )}
                    </div>
                  </td>
                  <td>
                    {isFolder || size === undefined ? "" : formatFileSize(size)}
                  </td>
                  <td className={styles["list-view__date-td"]}>
                    {isFolder
                      ? ""
                      : el.createDatetime &&
                        moment
                          .tz(el.createDatetime, defaultUserTimezone)
                          .format(DATE_FORMAT)}
                  </td>
                  {/* dont propagate to <tr> */}
                  {/* TODO: will be added in the new rev of BE api response */}
                  <td
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionsMenu
                      entry={el}
                      entryId={id}
                      storageDrive={storageDrive}
                      type={type}
                      downloadHref={`/drive/download/${id}`}
                      viewHref={`/drive/view/${id}`}
                      deleteEntry={deleteEntry}
                      onRename={openRename}
                      onDownload={() =>
                        onDownload &&
                        onDownload([
                          {
                            ...el,

                            type: isFolder
                              ? "folder"
                              : (el as OrchidDriveFile).mimeType,
                            guid: id,
                          },
                        ])
                      }
                      analyticsPageName={analyticsPageName}
                    />
                  </td>
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ListView;
